import { logUsage } from 'owa-analytics';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import { SaveToCloudStatus } from 'owa-attachment-savetocloud';
import { ClientItemId } from 'owa-client-ids';
import { ControlIcons } from 'owa-control-icons';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getExtensionWithoutDotFromFileName } from 'owa-file';
import fileExtensionToOfficeFileType from 'owa-file/lib/utils/fileExtensionToOfficeFileType';
import { FilterableMenuItem, FilterableMenuItemArgs } from 'owa-filterable-menu';
import loc from 'owa-localize';
import { SaveOneFileToGroupFilesText } from 'owa-locstrings/lib/strings/saveonefiletogroupfilestext.locstring.json';
import { SaveOneFileToOneDriveConsumerText } from 'owa-locstrings/lib/strings/saveonefiletoonedriveconsumertext.locstring.json';
import { SavingLabel } from 'owa-locstrings/lib/strings/savinglabel.locstring.json';
import { getUserConfiguration } from 'owa-session-store';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { lazySxSStoreModule, getOrCreateSxSStoreData, SxSStoreData } from 'owa-sxs-store';
import {
    EditInOneDriveSxSCommandBar,
    FailedSaveToOneDriveSxSText,
    SavedOneFileToOneDriveSxSText,
    ViewInOneDriveSxSCommandBar,
} from './getSaveToCloudButton.locstring.json';

export default function getSaveToCloudButton(sxsId: string) {
    const sxsStore = getOrCreateSxSStoreData(sxsId);
    const buttonLabel = getButtonLabel(
        sxsStore.extendedViewState.saveToCloudStatus,
        sxsStore.editState.itemId,
        sxsStore
    );
    return new FilterableMenuItem({
        [FilterableMenuItemArgs.name]: buttonLabel,
        [FilterableMenuItemArgs.icon]:
            (isFeatureEnabled('wvn-productizeSTCChicletAndPreview') ||
                (isFeatureEnabled('wvn-productizeSTCHoverAndCloud') && !isOfficeFile(sxsStore))) &&
            sxsStore.extendedViewState.saveToCloudStatus === SaveToCloudStatus.succeeded
                ? isOfficeFile(sxsStore)
                    ? ControlIcons.CloudEdit
                    : ControlIcons.Cloud
                : ControlIcons.CloudUpload,
        [FilterableMenuItemArgs.onClick]: async () => {
            if (
                (isFeatureEnabled('wvn-productizeSTCChicletAndPreview') ||
                    (isFeatureEnabled('wvn-productizeSTCHoverAndCloud') &&
                        !isOfficeFile(sxsStore))) &&
                sxsStore.extendedViewState.saveToCloudStatus === SaveToCloudStatus.succeeded
            ) {
                const attachmentId = sxsStore.extendedViewState.attachmentId;
                const attachment = attachmentId && getAttachment(attachmentId);
                if (attachment) {
                    const originalUrl = attachment.model.AttachmentOriginalUrl;
                    const querySeparator = originalUrl.indexOf('?') === -1 ? '?' : '&';
                    window.open(originalUrl + querySeparator + 'web=1', '_blank');
                    logUsage('WovenOpenAndEditSxSCommandBar', {}, { isCore: true });
                }
            } else {
                const sxsStoreModule = await lazySxSStoreModule.import();
                sxsStoreModule.saveToCloud('SxSCommandBar', sxsId);
            }
        },
        [FilterableMenuItemArgs.shouldShow]: () => {
            if (sxsStore.extendedViewState.saveToCloudStatus === SaveToCloudStatus.disabled) {
                return false;
            }

            if (isGroup(sxsStore.editState.itemId) && isConsumer()) {
                return false;
            }

            if (getUserConfiguration().SessionSettings.IsShadowMailbox) {
                return false;
            }

            return true;
        },
        [FilterableMenuItemArgs.disabled]:
            sxsStore.extendedViewState.saveToCloudStatus !== SaveToCloudStatus.available &&
            !(
                (isFeatureEnabled('wvn-productizeSTCChicletAndPreview') ||
                    (isFeatureEnabled('wvn-productizeSTCHoverAndCloud') &&
                        !isOfficeFile(sxsStore))) &&
                sxsStore.extendedViewState.saveToCloudStatus === SaveToCloudStatus.succeeded
            ),
    });
}

function getButtonLabel(
    saveToCloudStatus: SaveToCloudStatus,
    itemId: ClientItemId,
    sxsStore: SxSStoreData
) {
    switch (saveToCloudStatus) {
        case SaveToCloudStatus.available:
            return isGroup(itemId)
                ? loc(SaveOneFileToGroupFilesText)
                : loc(SaveOneFileToOneDriveConsumerText);
        case SaveToCloudStatus.saving:
            return loc(SavingLabel);
        case SaveToCloudStatus.succeeded:
            if (isFeatureEnabled('wvn-productizeSTCChicletAndPreview')) {
                return loc(
                    isOfficeFile(sxsStore)
                        ? EditInOneDriveSxSCommandBar
                        : ViewInOneDriveSxSCommandBar
                );
            }

            if (isFeatureEnabled('wvn-productizeSTCHoverAndCloud') && !isOfficeFile(sxsStore)) {
                return loc(ViewInOneDriveSxSCommandBar);
            }

            return loc(SavedOneFileToOneDriveSxSText);
        case SaveToCloudStatus.failed:
            return loc(FailedSaveToOneDriveSxSText);
        default:
            return '';
    }
}

function isGroup(itemId: ClientItemId) {
    return itemId?.mailboxInfo?.type === 'GroupMailbox';
}

function isOfficeFile(sxsStore: SxSStoreData) {
    const attachmentId = sxsStore.extendedViewState.attachmentId;
    const attachmentModel = attachmentId && getAttachment(attachmentId);
    return (
        fileExtensionToOfficeFileType(
            getExtensionWithoutDotFromFileName(attachmentModel?.model?.Name)
        ) !== ''
    );
}
